import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
} from '@mui/material';
import {
    CheckCircle,
    Cancel,
    HourglassEmpty,
    WhatsApp,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const PaymentRequests = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [rejectNotes, setRejectNotes] = useState('');

    useEffect(() => {
        fetchRequests();
    }, []);

    const fetchRequests = async () => {
        try {
            const token = localStorage.getItem('token');
            const { data } = await axios.get(`${API_BASE_URL}/api/payment/requests`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setRequests(data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching requests:', error);
            setLoading(false);
        }
    };

    const handleApprove = async (requestId) => {
        if (!window.confirm('هل أنت متأكد من تفعيل هذا الاشتراك؟')) return;

        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${API_BASE_URL}/api/payment/approve/${requestId}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            alert('✅ تم تفعيل الاشتراك بنجاح!');
            fetchRequests();
        } catch (error) {
            alert('حدث خطأ: ' + (error.response?.data?.message || error.message));
        }
    };

    const handleReject = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${API_BASE_URL}/api/payment/reject/${selectedRequest._id}`,
                { notes: rejectNotes },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            alert('تم رفض الطلب');
            setRejectDialogOpen(false);
            setRejectNotes('');
            fetchRequests();
        } catch (error) {
            alert('حدث خطأ: ' + (error.response?.data?.message || error.message));
        }
    };

    const getStatusChip = (status) => {
        const statusConfig = {
            pending: { label: 'قيد المراجعة', color: 'warning', icon: <HourglassEmpty /> },
            approved: { label: 'مفعّل', color: 'success', icon: <CheckCircle /> },
            rejected: { label: 'مرفوض', color: 'error', icon: <Cancel /> },
        };

        const config = statusConfig[status];
        return (
            <Chip
                label={config.label}
                color={config.color}
                icon={config.icon}
                size="small"
            />
        );
    };

    const pendingCount = requests.filter(r => r.status === 'pending').length;

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                    طلبات الدفع
                </Typography>
                {pendingCount > 0 && (
                    <Chip
                        label={`${pendingCount} طلب قيد المراجعة`}
                        color="warning"
                        sx={{ fontWeight: 'bold' }}
                    />
                )}
            </Box>

            {pendingCount > 0 && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                        💡 تواصل مع العملاء عبر واتساب للتأكد من استلام الدفع قبل التفعيل
                    </Typography>
                </Alert>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'primary.main' }}>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>المستخدم</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>البريد الإلكتروني</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>الخطة المطلوبة</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>المبلغ</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>الحالة</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>التاريخ</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>الإجراءات</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {requests.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                        لا توجد طلبات دفع
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            requests.map((request) => (
                                <TableRow key={request._id}>
                                    <TableCell>{request.user?.username}</TableCell>
                                    <TableCell>{request.user?.email}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={request.requestedPlan.toUpperCase()}
                                            color="primary"
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body1" fontWeight="bold">
                                            ${request.amount}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{getStatusChip(request.status)}</TableCell>
                                    <TableCell>
                                        {new Date(request.createdAt).toLocaleDateString('ar-EG')}
                                    </TableCell>
                                    <TableCell>
                                        {request.status === 'pending' ? (
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button
                                                    variant="contained"
                                                    color="success"
                                                    size="small"
                                                    onClick={() => handleApprove(request._id)}
                                                >
                                                    تفعيل
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    size="small"
                                                    onClick={() => {
                                                        setSelectedRequest(request);
                                                        setRejectDialogOpen(true);
                                                    }}
                                                >
                                                    رفض
                                                </Button>
                                            </Box>
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">
                                                {request.status === 'approved' ? 'تم التفعيل' : 'تم الرفض'}
                                                {request.approvedBy && ` بواسطة ${request.approvedBy.username}`}
                                            </Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Reject Dialog */}
            <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
                <DialogTitle>رفض طلب الدفع</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="سبب الرفض (اختياري)"
                        value={rejectNotes}
                        onChange={(e) => setRejectNotes(e.target.value)}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectDialogOpen(false)}>إلغاء</Button>
                    <Button onClick={handleReject} color="error" variant="contained">
                        رفض الطلب
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default PaymentRequests;
